import { Equities } from './components/Equities';
import { InstrumentTypeToggle } from './components/InstrumentTypeToggle';
import { Options } from './components/Options';
import type { SymbolInputRef } from './components/SymbolInput';
import { useIsOptionsSymbolValid } from './hooks/useIsOptionsSymbolValid';
import { useSymbolPickerManager } from './hooks/useSymbolPickerManager';
import { useRecentlyUsedSymbolsService } from './hooks/useSymbolSearchService';
import { logger } from './log';
import { SymbolPickerService } from './services/SymbolPickerService';
import {
    setInstrumentType,
    setUnderlyingSymbol,
    setDropdownSearchTerm,
    setDropdownItems,
    setDropdownActiveIndex,
    providedValueChanged,
    onPastedValue,
} from './state';
import type { InstrumentType, FocusedElement, SymbolPickerAction } from './state';
import { useSignals } from '@preact/signals-react/runtime';
import { cn, cva } from '@thinkalpha/ui-core/utils';
import type { VariantProps } from '@thinkalpha/ui-core/utils';
import { forwardRef, useEffect, useMemo } from 'react';
import { useContainer } from 'src/features/ioc';
import { useDisposable } from 'src/hooks/useDisposable';
import { useEvent } from 'src/hooks/useEvent';
import { isValidInstrument, parseInstrument } from 'src/lib/instrumentUtils';

const _log = logger.getSubLogger({ name: 'SymbolPicker/index' });

const symbolPickerVariants = cva('flex h-5 w-fit max-w-85 items-center gap-1 overflow-y-hidden rounded-md px-1', {
    variants: {
        variant: {
            default: 'border border-gray-700 bg-gray-600 focus-within:border-gray-500',
            light: 'border border-gray-300 bg-white text-black focus-within:border-gray-400',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

export interface SymbolPickerProps {
    value: string;
    onChange: (instrumentString: string) => void;
    className?: string;
    allowedInstrumentTypes: InstrumentType[];
    clearable?: boolean;
    /** When true, resets the picker to empty after onChange fires (e.g. Watchlist adding symbols to a list) */
    resetOnChange?: boolean;
    disabled?: boolean;
    placeholder?: string;
    variant?: VariantProps<typeof symbolPickerVariants>['variant'];
}

export const SymbolPicker = forwardRef<SymbolInputRef, SymbolPickerProps>(
    (
        {
            value: instrumentString,
            className,
            onChange,
            allowedInstrumentTypes: unstableAllowedInstrumentTypes,
            clearable,
            resetOnChange,
            disabled,
            placeholder,
            variant,
        },
        ref,
    ) => {
        useSignals();
        const recentlyUsedSymbolsService = useRecentlyUsedSymbolsService();
        const reactContainer = useContainer();
        const marketDataService = reactContainer.get('MarketDataService');

        // Create stable reference for allowedInstrumentTypes to prevent unnecessary re-renders
        const allowedTypesKey = unstableAllowedInstrumentTypes.join(',');
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
        const allowedInstrumentTypes = useMemo(() => unstableAllowedInstrumentTypes, [allowedTypesKey]);

        // onChange if passed in as a lambda, will be an unstable reference
        // wrapping in handleOnChange to prevent an easy to hit edge case causing update looping
        const handleOnChange = useEvent((instrumentString: string) => {
            onChange(instrumentString);
        });

        const symbolPickerService = useDisposable(
            () =>
                new SymbolPickerService({
                    recentlyUsedSymbolsService,
                    marketDataService,
                    initialInstrumentString: instrumentString,
                    initialAllowedInstrumentTypes: allowedInstrumentTypes,
                    onChange: handleOnChange,
                    resetOnChange,
                }),
            [],
        );

        const state = symbolPickerService.state.value;

        const dispatch = useEvent((action: SymbolPickerAction) => {
            symbolPickerService.dispatch(action);
        });

        const instrumentType =
            allowedInstrumentTypes.length === 1 ? allowedInstrumentTypes[0] : state.instrument.instrumentType;

        const { containerRef, handleInputFocus, handleContainerFocus, handleContainerBlur, handleContainerKeyDown } =
            useSymbolPickerManager(state, dispatch);

        // sync incoming value to our internal state ( reacts to parent value changes )
        useEffect(() => {
            dispatch(providedValueChanged(instrumentString));
        }, [dispatch, instrumentString]);

        // Simple event handlers - just dispatch actions, let reducer handle logic
        const handleInstrumentTypeChange = useEvent((instrumentType: InstrumentType) => {
            // Reset parent state when changing instrument types
            handleOnChange('');
            dispatch(setInstrumentType(instrumentType, 'forward'));
        });

        const handleUnderlyingSymbolChange = useEvent((symbol: string) => {
            dispatch(setUnderlyingSymbol(symbol, 'forward'));
        });

        const handleUnderlyingSymbolClear = useEvent(() => {
            // Reset parent state when changing instrument types
            handleOnChange('');
            dispatch(setUnderlyingSymbol('', 'focus'));
        });

        // Element-specific dropdown search handlers
        const handleDropdownSearchForElement = useEvent((element: FocusedElement, term: string) => {
            dispatch(setDropdownSearchTerm(element, term));
        });

        const handleDropdownItems = useEvent((element: FocusedElement, items: any[]) => {
            dispatch(setDropdownItems(element, items));
        });

        const handleDropdownActiveIndex = useEvent((index: number) => {
            if (!state.focusedElement) return;
            dispatch(setDropdownActiveIndex(state.focusedElement, index));
        });

        const handlePaste = useEvent((e: React.ClipboardEvent<HTMLDivElement>) => {
            const text = e.clipboardData?.getData('text') ?? '';
            const trimmedText = text.trim();

            // If pasted text contains newlines, commas or spaces, split and try to parse each part as a symbol
            const potentialSymbols = trimmedText.split(/\n|,|\s+/).filter((s) => s.length > 0);
            if (potentialSymbols.length > 1) {
                potentialSymbols.forEach((symbol) => {
                    try {
                        const trimmedSymbol = symbol.trim();
                        const instrument = parseInstrument(trimmedSymbol);
                        const instrumentType = instrument.type === 'option' ? 'options' : instrument.type;
                        if (isValidInstrument(instrument) && allowedInstrumentTypes.includes(instrumentType)) {
                            onChange(trimmedSymbol);
                        }
                    } catch (_error) {}
                });
                e.preventDefault();
                e.stopPropagation();
                return;
            }

            // If only equity is allowed, let paste fall through
            if (!allowedInstrumentTypes.includes('options')) {
                return;
            }

            // If the pasted text is exactly 21 characters and options are allowed, try to parse as options symbol
            if (trimmedText.length === 21) {
                e.preventDefault();
                e.stopPropagation();
                dispatch(onPastedValue(trimmedText));
            }
            // For any other length, let the paste fall through to the focused input
        });

        const doesOptionSymbolExist = useIsOptionsSymbolValid({
            symbolType: state.instrument.instrumentType,
            underlyingSymbol: state.instrument.underlyingSymbol,
            expirationDate: state.instrument.expirationDate,
            optionCallPut: state.instrument.optionCallPut,
            strikePrice: state.instrument.strikePrice,
        });

        return (
            <div
                ref={containerRef}
                tabIndex={0}
                role="group"
                aria-label="Symbol picker"
                className={cn(
                    symbolPickerVariants({ variant }),
                    disabled && 'pointer-events-none cursor-not-allowed opacity-50',
                    !doesOptionSymbolExist && 'border-yellow-500/40 bg-yellow-500/20',
                    className,
                )}
                onFocus={handleContainerFocus}
                onBlur={handleContainerBlur}
                onKeyDown={handleContainerKeyDown}
                onPaste={handlePaste}
            >
                <InstrumentTypeToggle
                    allowedInstrumentTypes={allowedInstrumentTypes}
                    instrumentType={state.instrument.instrumentType}
                    onChange={handleInstrumentTypeChange}
                    dropdownOpen={state.focusedElement === 'instrumentType'}
                    dropdown={state.dropdowns.instrumentType}
                    onDropdownActiveIndex={handleDropdownActiveIndex}
                    onFocus={handleInputFocus}
                />

                {instrumentType === 'equity' ? (
                    <Equities
                        ref={ref}
                        state={state}
                        onUnderlyingSymbolChange={handleUnderlyingSymbolChange}
                        onUnderlyingSymbolClear={handleUnderlyingSymbolClear}
                        onDropdownSearchForElement={handleDropdownSearchForElement}
                        onDropdownItems={handleDropdownItems}
                        onDropdownActiveIndex={handleDropdownActiveIndex}
                        onFocus={handleInputFocus}
                        clearable={clearable}
                        placeholder={placeholder}
                        recentlyUsedSymbolsService={recentlyUsedSymbolsService}
                    />
                ) : (
                    <Options
                        state={state}
                        dispatch={dispatch}
                        onUnderlyingSymbolChange={handleUnderlyingSymbolChange}
                        onUnderlyingSymbolClear={handleUnderlyingSymbolClear}
                        onDropdownSearchForElement={handleDropdownSearchForElement}
                        onDropdownItems={handleDropdownItems}
                        onDropdownActiveIndex={handleDropdownActiveIndex}
                        onFocus={handleInputFocus}
                        clearable={clearable}
                        recentlyUsedSymbolsService={recentlyUsedSymbolsService}
                    />
                )}
            </div>
        );
    },
);

SymbolPicker.displayName = 'SymbolPicker';
